import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2 } from 'lucide-react';

const emptyForm = {
  name: '',
  company: '',
  email: '',
  phone: '',
  projectType: '',
  description: '',
  budget: '',
  timeline: '',
  existingWebsite: '',
  preferredContact: 'email',
};

const IntakeModal = ({ open, onClose }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.projectType) {
      toast({ title: 'Missing info', description: 'Please fill in name, email, and project type.' });
      return;
    }
    // Save to local storage (mock)
    const existing = JSON.parse(localStorage.getItem('blkpixel_leads') || '[]');
    existing.push({ ...form, id: Date.now(), createdAt: new Date().toISOString() });
    localStorage.setItem('blkpixel_leads', JSON.stringify(existing));
    setSubmitted(true);
    toast({ title: 'Request received', description: 'We\'ll be in touch within 24 hours.' });
  };

  const handleClose = () => {
    setForm(emptyForm);
    setSubmitted(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl bg-[#0a0a12] border border-white/10 text-white">
        {submitted ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#7c3aed] to-[#4f46e5] flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-white text-xl font-bold">Thanks, {form.name || 'friend'}!</h3>
            <p className="text-gray-400 text-sm mt-2">
              Your project request has been received. Our team will review it and reach out via {form.preferredContact} within 24 hours.
            </p>
            <Button
              onClick={handleClose}
              className="mt-6 h-10 px-6 rounded-md bg-gradient-to-r from-[#7c3aed] to-[#4f46e5] hover:from-[#8b45f0] hover:to-[#5b52ee] text-white border-0"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-white text-xl">Start Your Project</DialogTitle>
              <DialogDescription className="text-gray-400">
                Tell us about your project and we will get back within 24 hours.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4 mt-2">
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => update('name', e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Company</Label>
                <Input value={form.company} onChange={(e) => update('company', e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-gray-300 text-xs">Project Type *</Label>
                <Select value={form.projectType} onValueChange={(v) => update('projectType', v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a12] border-white/10 text-white">
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="webapp">Web App</SelectItem>
                    <SelectItem value="mobile">Mobile App</SelectItem>
                    <SelectItem value="saas">SaaS Platform</SelectItem>
                    <SelectItem value="automation">Automation</SelectItem>
                    <SelectItem value="ai">AI Solution</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-gray-300 text-xs">Project Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => update('description', e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Estimated Budget</Label>
                <Select value={form.budget} onValueChange={(v) => update('budget', v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select budget" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a12] border-white/10 text-white">
                    <SelectItem value="under-5k">Under $5,000</SelectItem>
                    <SelectItem value="5k-15k">$5,000 - $15,000</SelectItem>
                    <SelectItem value="15k-50k">$15,000 - $50,000</SelectItem>
                    <SelectItem value="50k+">$50,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Desired Timeline</Label>
                <Select value={form.timeline} onValueChange={(v) => update('timeline', v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a12] border-white/10 text-white">
                    <SelectItem value="asap">ASAP</SelectItem>
                    <SelectItem value="1-3">1-3 months</SelectItem>
                    <SelectItem value="3-6">3-6 months</SelectItem>
                    <SelectItem value="6+">6+ months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 flex justify-end gap-3 mt-2">
                <Button type="button" variant="outline" onClick={handleClose} className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-6 rounded-md bg-gradient-to-r from-[#7c3aed] to-[#4f46e5] hover:from-[#8b45f0] hover:to-[#5b52ee] text-white border-0 shadow-[0_0_20px_rgba(124,58,237,0.35)]"
                >
                  Submit Request
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IntakeModal;
